// DashboardSummaryWidget.jsx
import React, { useMemo, forwardRef, useImperativeHandle } from "react";
import {
  dashboardBuildings,
  dashboardMoney,
  dashboardWantListings,
  dashboardTime,
} from "@/images";
import { useAgentDashboardMetrics } from "@/hooks";
import { thousandSeperatedValue } from "@/helpers/v2/common";
import useAgentGitchaRestrictions from "@/hooks/useAgentGitchaRestrictions";
import { useNavigate } from "react-router-dom";
import { pageURLs } from "@/constants";
import HandshakeIcon from "@/images/handshake.svg";
import "./DashboardSummaryWidget.scss";

const DashboardSummaryWidget = forwardRef(({ scope = "inventory", onFilter, onSort, onReviewForms }, ref) => {

  const { shouldRestrictAgent } = useAgentGitchaRestrictions();

  const { data, loading, refetch } = useAgentDashboardMetrics(scope);
  const navigate = useNavigate();

  useImperativeHandle(ref, () => ({ refetch }), [refetch]);

  const items = useMemo(() => {
    const isTwoItemScope = scope === "inventory" || scope === "mls";
    if (loading)
      return Array(isTwoItemScope ? 2 : 4).fill({});

    if (!data) return [];

    if (isTwoItemScope)
      return [
        {
          title: "Potential Seller Home Values",
          value:
            data.potentialSellerValue != null
              ? `$${Number(data.potentialSellerValue).toLocaleString("en-US", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}`
              : "-",
          circleClass: "dashboard-circle-green",
          img: dashboardMoney,
          sortKey: "price",
        },
        {
          title: "Total Buyer Matches",
          value: thousandSeperatedValue(data.totalBuyerMatches) ?? "-",
          circleClass: "dashboard-circle-green",
          img: dashboardBuildings,
          sortKey: "potentialbuyers",
        },
      ];

    return [
      {
        title: shouldRestrictAgent ? "Syndicated Active Buyer Pipeline" : "Active Buyer Pipeline",
        value:
          data.activeBuyerPipeline != null
            ? `$${Number(data.activeBuyerPipeline).toLocaleString("en-US", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}`
            : "-",
        circleClass: "dashboard-circle-green",
        img: dashboardMoney,
        filterKey: "active",
      },
      {
        title: shouldRestrictAgent ? "Syndicated Active Want-Listings" : "Active Want-Listings",
        value: data.currentWantListings ?? "-",
        circleClass: "dashboard-circle-green",
        img: dashboardWantListings,
        filterKey: "active",
      },
      {
        title: "Forms Waiting Review",
        value: data.formsWaitingReview ?? "-",
        circleClass: "dashboard-circle-orange",
        img: dashboardTime,
        reviewsForms: Number(data.formsWaitingReview) > 0,
      },
      {
        title: "Introductions Waiting",
        value: data.introductionsWaiting ?? "-",
        circleClass: "dashboard-circle-orange",
        img: HandshakeIcon,
        href: `${pageURLs.allIntroductions}?view=all`,
      },
    ].filter(Boolean);
  }, [loading, data, scope, shouldRestrictAgent]);

  return (
    <div className="dashboard-summary-widget d-flex justify-content-between align-items-center p-4">
      {items.map(({ title, value, note, circleClass, img, filterKey, href, sortKey, reviewsForms }, index) => {
        const act = href
          ? () => navigate(href)
          : filterKey && onFilter
            ? () => onFilter(filterKey)
            : sortKey && onSort
              ? () => onSort(sortKey)
              : reviewsForms && onReviewForms
                ? onReviewForms
                : null;
        const actionable = Boolean(act) && !loading;
        return (
        <div
          key={title || index}
          className={`summary-item d-flex align-items-center flex-grow-1${actionable ? " is-actionable" : ""}`}
          role={actionable ? "button" : undefined}
          tabIndex={actionable ? 0 : undefined}
          onClick={actionable ? act : undefined}
          onKeyDown={
            actionable
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    act();
                  }
                }
              : undefined
          }
        >
          <div
            className={`icon-wrapper me-3 ${loading ? "dashboard-placeholder-circle" : circleClass}`}
          >
            {!loading && (
              <img src={img} className="dashboard-summary-icon" alt="" />
            )}
          </div>
          <div className="text-wrapper w-100">
            <h6 className="mb-1 dashboard-text-secondary">
              {loading ? (
                <div className="dashboard-placeholder-line w-75" />
              ) : (
                title
              )}
            </h6>
            <h3 className="mb-1 dashboard-text-tertiary">
              {loading ? (
                <div className="dashboard-placeholder-line w-50" />
              ) : (
                value
              )}
            </h3>
            {!loading && note ? (
              <p className={`dashboard-summary-note dashboard-summary-note--${note.tone}`}>{note.text}</p>
            ) : null}
          </div>
        </div>
        );
      })}
    </div>
  );
});

export default DashboardSummaryWidget;
